import { servicePost, servicePostBatchStream, onEndpointEpochChange, type BatchCall } from "./client.ts";
import { type ReturnObject, ErrorType } from "../types/returnObject.ts";

/*
 * Opt-in request coalescer for READ calls.
 *
 * Every call made through `batchedServicePost` within the same microtask tick —
 * a page/modal boot effect plus any child components mounting in the same commit —
 * is collected into a SINGLE `/api/v2/services-stream` request. Each call's promise
 * resolves the instant its slot streams back, so the UI still fills progressively,
 * but the whole fan-out costs one round-trip + one JWT validation.
 *
 * READ-ONLY by contract. Identical method+args are de-duplicated (also absorbs
 * StrictMode double-mounts) — which is safe for reads but would silently drop a
 * duplicate write, so never route mutations through here. Use plain `servicePost`
 * for writes, ordered sequences, or anything that needs a per-call AbortSignal.
 *
 * On stream failure each unresolved slot resolves to an error envelope, so callers'
 * existing `bError`/`oData` handling produces their normal empty defaults.
 *
 * The de-dup map is endpoint-scoped: an active-endpoint change settles everything
 * outstanding and empties the map. Without that, switching endpoints while a read
 * is in flight and landing on a page that asks for the same method+args would hand
 * back the PREVIOUS endpoint's promise — the new endpoint silently rendering the
 * old one's data.
 */

interface QueueItem {
  key: string;
  method: string;
  args?: Record<string, unknown>;
  resolve: (r: ReturnObject<unknown>) => void;
}

const inflight = new Map<string, Promise<ReturnObject<unknown>>>();
/** Every queued-or-in-flight item, so an endpoint change can settle them all. */
const outstanding = new Map<string, QueueItem>();
let queue: QueueItem[] = [];
let scheduled = false;

function stableKey(method: string, args?: Record<string, unknown>): string {
  let a = "{}";
  try {
    a = JSON.stringify(args ?? {});
  } catch {
    a = "{}";
  }
  return `${method}:${a}`;
}

function errorEnvelope(message: string): ReturnObject<unknown> {
  return { bError: true, eErrorType: ErrorType.UnknownError, sMessage: message, oData: null } as ReturnObject<unknown>;
}

async function flush(): Promise<void> {
  scheduled = false;
  const batch = queue;
  queue = [];
  if (batch.length === 0) return;

  const settled = new Set<number>();
  const finishSlot = (i: number, r: ReturnObject<unknown>) => {
    const item = batch[i];
    if (!item || settled.has(i)) return;
    settled.add(i);
    // Only evict our own entry: an endpoint change may have cleared the maps and
    // a fresh call could already hold this key. Deleting blindly would drop the
    // new caller's de-dup entry.
    if (outstanding.get(item.key) === item) {
      outstanding.delete(item.key);
      inflight.delete(item.key);
    }
    item.resolve(r);
  };

  // Batch of one — nothing to coalesce. A direct call is cheaper than spinning up
  // the stream, and keeps lone wrapper calls behaving exactly like `servicePost`.
  if (batch.length === 1) {
    const only = batch[0]!;
    try {
      finishSlot(0, await servicePost(only.method, only.args));
    } catch {
      finishSlot(0, errorEnvelope("Read call failed"));
    }
    return;
  }

  const calls: BatchCall[] = batch.map((b) => ({ method: b.method, args: b.args }));

  try {
    await servicePostBatchStream(calls, (index, result) => finishSlot(index, result));
  } catch {
    // Network / stream failure — the finally block resolves the rest as errors.
  } finally {
    for (let i = 0; i < batch.length; i++) {
      finishSlot(i, errorEnvelope("Read batch stream did not return this slot"));
    }
  }
}

/**
 * Queue a READ call into the current-tick batch. Returns a `ReturnObject` that
 * resolves when this slot streams in. Same shape as `servicePost`, so callers keep
 * their existing `bError`/`oData` parsing untouched. Reads only — see file header.
 */
export function batchedServicePost<T>(method: string, args?: Record<string, unknown>): Promise<ReturnObject<T>> {
  const key = stableKey(method, args);
  const existing = inflight.get(key);
  if (existing) return existing as Promise<ReturnObject<T>>;

  let item!: QueueItem;
  const p = new Promise<ReturnObject<unknown>>((resolve) => {
    item = { key, method, args, resolve };
    queue.push(item);
  });
  inflight.set(key, p);
  outstanding.set(key, item);
  if (!scheduled) {
    scheduled = true;
    queueMicrotask(flush);
  }
  return p as Promise<ReturnObject<T>>;
}

/*
 * Active-endpoint change: drop the pending queue and settle everything still
 * outstanding as an error envelope (callers fall back to their empty defaults).
 * The underlying stream is already aborted by the epoch's AbortController; this
 * releases the awaiting callers and — critically — empties the de-dup map so the
 * next endpoint re-fetches instead of adopting the old endpoint's promise.
 */
onEndpointEpochChange(() => {
  queue = [];
  scheduled = false;
  const pending = [...outstanding.values()];
  outstanding.clear();
  inflight.clear();
  for (const item of pending) item.resolve(errorEnvelope("Active endpoint changed"));
});

/**
 * Drop-in for `servicePostBatch`: takes a `BatchCall[]`, returns each slot's
 * `ReturnObject` in input order. Difference: instead of its own `/api/v2/services`
 * request, every call rides the shared coalescer, so it merges with any other
 * `batchedServicePost` reads fired in the same tick into one stream. READ-ONLY.
 */
export async function servicePostBatched<TResults extends unknown[] = unknown[]>(
  calls: BatchCall[],
): Promise<{ [K in keyof TResults]: ReturnObject<TResults[K]> }> {
  const results = await Promise.all(calls.map((c) => batchedServicePost(c.method, c.args)));
  return results as { [K in keyof TResults]: ReturnObject<TResults[K]> };
}
